"use client";

import {
  CopyButton,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { CARD, RESULT_CARD, WIDGET_H3 } from "@/components/tool-kit/styles";
import type { DnaConcentrationCalculatorStrings } from "@/components/tools/dna-concentration-calculator";
import { t } from "@/lib/i18n/interpolate";
import {
  fixed,
  type NumberFormatter,
  plain,
  sig,
} from "@/lib/tool-kit/biology-lab/format";
import {
  type AbsorbanceError,
  type AbsorbanceInput,
  type AbsorbanceResult,
  type Amounts,
  fromAbsorbance,
  G_PER_MOL_PER_UNIT,
  judgeRatio230,
  judgeRatio280,
  type NucleicKind,
  type RatioVerdict,
} from "@/lib/tool-kit/biology-lab/nucleic-acid";

type Strings = DnaConcentrationCalculatorStrings;

const HIGH_A260 = 1.5;
const LOW_A260 = 0.1;
const RATIO_PLACES = 2;
const WORKING_BOX =
  "space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed";

export function kindLabel(kind: NucleicKind, strings: Strings): string {
  const labels: Record<NucleicKind, string> = {
    dsdna: strings.kindDsdna,
    ssdna: strings.kindSsdna,
    rna: strings.kindRna,
    oligo: strings.kindOligo,
  };
  return labels[kind];
}

export function kindShort(kind: NucleicKind, strings: Strings): string {
  const labels: Record<NucleicKind, string> = {
    dsdna: strings.kindShortDsdna,
    ssdna: strings.kindShortSsdna,
    rna: strings.kindShortRna,
    oligo: strings.kindShortOligo,
  };
  return labels[kind];
}

function errorText(code: AbsorbanceError, strings: Strings): string {
  const messages: Record<AbsorbanceError, string> = {
    path: strings.errPath,
    dilution: strings.errDilution,
    factor: strings.errFactor,
    background: strings.errBackground,
    nonPositive: strings.errNonPositive,
  };
  return messages[code];
}

function verdictText(
  verdict: RatioVerdict,
  messages: { low: string; high: string },
  strings: Strings
): string {
  if (verdict === "ok") {
    return strings.ok;
  }
  return verdict === "low" ? messages.low : messages.high;
}

function rangeWarning(a260: number, strings: Strings): string | null {
  if (a260 > HIGH_A260) {
    return strings.warnHigh;
  }
  return a260 < LOW_A260 ? strings.warnLow : null;
}

function RatioCard({
  label,
  value,
  expected,
  verdict,
  fmt,
}: {
  label: string;
  value: number;
  expected: string;
  verdict: string;
  fmt: NumberFormatter;
}) {
  return (
    <div className={`${CARD} space-y-1`}>
      <p className="font-semibold text-foreground text-lg">
        {label} = {fixed(fmt, value, RATIO_PLACES)}
      </p>
      <p className="text-muted-foreground text-xs">{expected}</p>
      <p className="text-foreground text-sm">{verdict}</p>
    </div>
  );
}

function Purity({
  result,
  kind,
  fmt,
  strings,
}: {
  result: AbsorbanceResult;
  kind: NucleicKind;
  fmt: NumberFormatter;
  strings: Strings;
}) {
  const isRna = kind === "rna";
  if (result.ratio280 === null && result.ratio230 === null) {
    return <Notice>{strings.ratioNone}</Notice>;
  }
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.purityHeading}</h3>
      <div className="grid gap-4 sm:grid-cols-2">
        {result.ratio280 === null ? null : (
          <RatioCard
            expected={isRna ? strings.expectedRna : strings.expectedDna}
            fmt={fmt}
            label={strings.ratio280}
            value={result.ratio280}
            verdict={verdictText(
              judgeRatio280(result.ratio280, kind),
              {
                low: strings.low280,
                high: isRna ? strings.high280Rna : strings.high280Dna,
              },
              strings
            )}
          />
        )}
        {result.ratio230 === null ? null : (
          <RatioCard
            expected={strings.expected230}
            fmt={fmt}
            label={strings.ratio230}
            value={result.ratio230}
            verdict={verdictText(
              judgeRatio230(result.ratio230),
              { low: strings.low230, high: strings.high230 },
              strings
            )}
          />
        )}
      </div>
    </div>
  );
}

function summaryText(
  result: AbsorbanceResult,
  input: AbsorbanceInput,
  strings: Strings,
  fmt: NumberFormatter
): string {
  const parts = [
    t(strings.copySummary, {
      kind: kindShort(input.kind, strings),
      c: sig(fmt, result.concentration, 4),
    }),
  ];
  if (result.ratio280 !== null) {
    parts.push(
      t(strings.copyRatio280, {
        value: fixed(fmt, result.ratio280, RATIO_PLACES),
      })
    );
  }
  if (result.ratio230 !== null) {
    parts.push(
      t(strings.copyRatio230, {
        value: fixed(fmt, result.ratio230, RATIO_PLACES),
      })
    );
  }
  if (result.totalUg !== null && input.volumeUl !== null) {
    parts.push(
      t(strings.copyYield, {
        value: sig(fmt, result.totalUg, 4),
        volume: plain(fmt, input.volumeUl),
      })
    );
  }
  return parts.join("; ");
}

function AbsorbanceResults({
  input,
  fmt,
  strings,
}: {
  input: AbsorbanceInput;
  fmt: NumberFormatter;
  strings: Strings;
}) {
  const outcome = fromAbsorbance(input);
  if ("error" in outcome) {
    return <ErrorNotice>{errorText(outcome.error, strings)}</ErrorNotice>;
  }
  const r = outcome.result;
  const warning = rangeWarning(r.a260, strings);
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={`${strings.concentration} (${strings.unitNgUl})`}
          value={sig(fmt, r.concentration, 4)}
        />
        {r.totalUg === null ? null : (
          <StatCard
            label={`${strings.totalYield} (${strings.unitUg})`}
            value={sig(fmt, r.totalUg, 4)}
          />
        )}
        {r.pmolPerUl === null ? null : (
          <StatCard
            label={`${strings.molarity} (${strings.unitNm})`}
            value={sig(fmt, r.pmolPerUl * 1000, 4)}
          />
        )}
        {r.totalPmol === null ? null : (
          <StatCard
            label={`${strings.totalPmol} (${strings.unitPmol})`}
            value={sig(fmt, r.totalPmol, 4)}
          />
        )}
      </StatGrid>
      <p className="text-muted-foreground text-xs">
        {strings.perMlNote}{" "}
        {input.dilution === 1
          ? null
          : t(strings.measuredLine, { value: sig(fmt, r.measured, 4) })}
      </p>
      {warning ? <Notice>{warning}</Notice> : null}
      <Purity fmt={fmt} kind={input.kind} result={r} strings={strings} />
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        <p>{strings.workingFormula}</p>
        <p>
          {t(strings.workingNumbers, {
            a: plain(fmt, r.a260),
            factor: plain(fmt, r.factor),
            path: plain(fmt, input.pathMm),
            dilution: plain(fmt, input.dilution),
            c: sig(fmt, r.concentration, 4),
          })}
        </p>
        {r.pmolPerUl !== null && input.length !== null ? (
          <p>
            {t(strings.workingMoles, {
              c: sig(fmt, r.concentration, 4),
              mw: plain(fmt, G_PER_MOL_PER_UNIT[input.kind]),
              length: plain(fmt, input.length),
              value: sig(fmt, r.pmolPerUl, 4),
            })}
          </p>
        ) : null}
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        small
        text={summaryText(r, input, strings, fmt)}
      />
    </>
  );
}

function ConvertResults({
  amounts,
  kind,
  length,
  fmt,
  strings,
}: {
  amounts: Amounts;
  kind: NucleicKind;
  length: number;
  fmt: NumberFormatter;
  strings: Strings;
}) {
  const isDs = kind === "dsdna";
  const unit = isDs ? strings.unitBp : strings.unitNt;
  const mw = G_PER_MOL_PER_UNIT[kind];
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.unitOptionNg}
          value={sig(fmt, amounts.ng, 4)}
        />
        <StatCard
          label={strings.unitOptionUg}
          value={sig(fmt, amounts.ug, 4)}
        />
        <StatCard
          label={strings.unitOptionPmol}
          value={sig(fmt, amounts.pmol, 4)}
        />
        <StatCard
          label={strings.unitOptionFmol}
          value={sig(fmt, amounts.fmol, 4)}
        />
        {isDs ? (
          <StatCard
            label={strings.endsLabel}
            value={sig(fmt, amounts.pmolEnds, 4)}
          />
        ) : null}
      </StatGrid>
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        <p>
          {t(strings.convertWorking, {
            length: plain(fmt, length),
            unit,
            mw: plain(fmt, mw),
            total: plain(fmt, mw * length),
          })}
        </p>
      </div>
      <p className="text-muted-foreground text-xs">
        {t(strings.convertNote, { mw: plain(fmt, mw), unit })}
      </p>
    </>
  );
}

export function AbsorbancePanel({
  input,
  fmt,
  strings,
}: {
  input: AbsorbanceInput | null;
  fmt: NumberFormatter;
  strings: Strings;
}) {
  if (input === null) {
    return <Notice>{strings.enterA260}</Notice>;
  }
  return (
    <div className={`${RESULT_CARD} space-y-4`}>
      <AbsorbanceResults fmt={fmt} input={input} strings={strings} />
    </div>
  );
}

export function ConvertPanel({
  amounts,
  kind,
  length,
  fmt,
  strings,
}: {
  amounts: Amounts | null;
  kind: NucleicKind;
  length: number | null;
  fmt: NumberFormatter;
  strings: Strings;
}) {
  if (amounts === null || length === null) {
    return <Notice>{strings.convertEnter}</Notice>;
  }
  return (
    <ConvertResults
      amounts={amounts}
      fmt={fmt}
      kind={kind}
      length={length}
      strings={strings}
    />
  );
}
